import type { Ref } from 'vue';

import { computed, onUnmounted, watch } from 'vue';

import useDirectionalSlideAnimation from '@/composables/useDirectionalSlideAnimation';
import useJobCarousel, {
  type JobCarouselItem,
} from '@/composables/useJobCarousel';
import { useJobEditStore } from '@/stores/useJobEditStore';

interface UseJobEditCarouselControllerOptions {
  jobs: Ref<JobCarouselItem[]>;
  jobId: Ref<number | undefined>;
  jobDate: Ref<string | undefined>;
  onDataChanged: Ref<(() => Promise<void>) | undefined>;
}

export function useJobEditCarouselController(
  options: UseJobEditCarouselControllerOptions,
) {
  const jobEditStore = useJobEditStore();
  const carousel = useJobCarousel();

  const slideAnimation = useDirectionalSlideAnimation({
    classMap: {
      slideOutNext: 'job-edit-slide-out-next',
      slideOutPrev: 'job-edit-slide-out-prev',
      slideInNext: 'job-edit-slide-in-next',
      slideInPrev: 'job-edit-slide-in-prev',
    },
    slideOutDurationMs: 500,
    slideInDurationMs: 500,
  });

  const hasJobData = computed(() => Boolean(jobEditStore.jobData));
  const isJobLoading = computed(() => jobEditStore.isLoading);
  const isEditingSection = computed(() => jobEditStore.lockState.isLocked);

  const showInitialLoader = computed(() => !hasJobData.value);
  const showContentLoader = computed(
    () => hasJobData.value && isJobLoading.value,
  );
  const isNavigationDisabled = computed(() => isJobLoading.value);
  const showFooter = computed(
    () => !isEditingSection.value && carousel.totalJobs.value > 0,
  );

  function setupCarouselFromInput(): void {
    const jobs = options.jobs.value;
    const jobId = options.jobId.value;
    const jobDate = options.jobDate.value;

    if (jobs.length > 0) {
      let startIndex = 0;

      if (jobId && jobDate) {
        startIndex = jobs.findIndex(
          (job) => job.jobId === jobId && job.jobDate === jobDate,
        );

        if (startIndex === -1) {
          startIndex = 0;
        }
      }

      carousel.setJobs(jobs, startIndex);
      return;
    }

    if (jobId && jobDate) {
      carousel.setJobs([{ jobId, jobDate }], 0);
      return;
    }

    carousel.reset();
  }

  async function loadCurrentJob(jobId: number, jobDate: string): Promise<void> {
    jobEditStore.init(jobId, jobDate);
    await jobEditStore.loadJob();
  }

  function handlePreviousClick(): void {
    if (isNavigationDisabled.value) {
      return;
    }

    slideAnimation.startNavigation('prev');
    carousel.goToPrevious();
  }

  function handleNextClick(): void {
    if (isNavigationDisabled.value) {
      return;
    }

    slideAnimation.startNavigation('next');
    carousel.goToNext();
  }

  watch(
    () => options.onDataChanged.value,
    (onDataChanged) => {
      jobEditStore.setOnDataChanged(onDataChanged ?? null);
    },
    { immediate: true },
  );

  watch(
    () => [options.jobs.value, options.jobId.value, options.jobDate.value],
    () => {
      setupCarouselFromInput();
    },
    { immediate: true },
  );

  watch(
    () => carousel.currentJob.value,
    async (job) => {
      if (!job) {
        return;
      }

      try {
        await loadCurrentJob(job.jobId, job.jobDate);
        slideAnimation.finishNavigation();
      } catch {
        slideAnimation.cancelNavigation();
      }
    },
    { immediate: true },
  );

  onUnmounted(() => {
    slideAnimation.cancelNavigation();
    jobEditStore.setOnDataChanged(null);
    jobEditStore.$reset();
    carousel.reset();
  });

  return {
    carousel,
    slideAnimation,
    showInitialLoader,
    showContentLoader,
    showFooter,
    isNavigationDisabled,
    handlePreviousClick,
    handleNextClick,
  };
}
